import type { SessionListItem } from '@fos/shared/contracts/identity';
import { LogOut, Monitor, Smartphone } from 'lucide-react';
import { type FormEvent, useMemo, useRef, useState } from 'react';
import { useSignOut } from '../../../app/layout/UserMenus';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { useActiveSession } from '../../../lib/auth';
import { formatRelativePast } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { useFocusFirstInvalid } from '../../../lib/useFocusInvalid';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { PasswordInput } from '../../../ui/Input';
import { ListRow } from '../../../ui/ListRow';
import { SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { Status } from '../../../ui/Status';
import { toast } from '../../../ui/Toast';
import { PasswordChecklist } from '../../auth/components/PasswordChecklist';
import { passwordChecks, passwordProblem } from '../../auth/password-policy';
import { TOUCH_BUTTON } from '../../mobile';
import { SettingsHeader } from './SettingsLayout';
import { TwoFactorSection } from './TwoFactorSection';

/** Ustawienia → Bezpieczeństwo: hasło, urządzenia, kod z maila (2FA) i zaufane urządzenia. Czeka na projekt w Figmie. */
export function SecurityPage() {
  return (
    <div>
      <SettingsHeader
        title="Bezpieczeństwo"
        description="Hasło, urządzenia, na których jesteś zalogowany, i kod z maila przy logowaniu."
      />
      <ChangePasswordSection />
      <DevicesSection />
      <TwoFactorSection />
    </div>
  );
}

type PasswordField = 'currentPassword' | 'newPassword' | 'confirm';

function ChangePasswordSection() {
  const session = useActiveSession();
  const utils = trpc.useUtils();
  const changePassword = trpc.identity.changePassword.useMutation();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<PasswordField, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  useFocusFirstInvalid(formRef, errors);

  const checks = useMemo(
    () => passwordChecks(next, { email: session.user.email, name: session.user.name, confirm }),
    [next, confirm, session.user.email, session.user.name],
  );
  const problem = passwordProblem(checks, next);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setFormError(null);
    const fieldErrors: Partial<Record<PasswordField, string>> = {};
    if (!current) fieldErrors.currentPassword = 'Podaj obecne hasło';
    if (problem === 'Hasła się różnią') fieldErrors.confirm = problem;
    else if (problem) fieldErrors.newPassword = problem;
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    try {
      await changePassword.mutateAsync({ currentPassword: current, newPassword: next });
      setCurrent('');
      setNext('');
      setConfirm('');
      setSubmitted(false);
      toast.success('Hasło zmienione', { description: 'Pozostałe urządzenia zostały wylogowane.' });
      void utils.identity.listSessions.invalidate();
    } catch (err) {
      const issues = validationIssues(err);
      const mapped: Partial<Record<PasswordField, string>> = {};
      for (const issue of issues) {
        if (issue.path === 'currentPassword' || issue.path === 'newPassword') mapped[issue.path] = issue.message;
      }
      if (Object.keys(mapped).length > 0) {
        setErrors(mapped);
        return;
      }
      const code = appErrorCode(err);
      if (code === 'UNAUTHENTICATED' || code === 'FORBIDDEN') {
        setErrors({ currentPassword: 'Obecne hasło jest nieprawidłowe' });
        return;
      }
      if (code === 'STEP_UP_REQUIRED') return; // anulowane potwierdzenie hasłem
      setFormError(errorMessage(err));
    }
  };

  return (
    <Section className="mt-6" aria-labelledby="change-password">
      <SectionHeader tone="strong" title={<span id="change-password">Zmień hasło</span>} />
      <form
        ref={formRef}
        onSubmit={submit}
        noValidate
        className="mt-2 flex max-w-[420px] flex-col gap-4"
        aria-label="Zmiana hasła"
      >
        <Field label="Obecne hasło" error={errors.currentPassword}>
          <PasswordInput autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
        </Field>
        <Field label="Nowe hasło" error={submitted ? errors.newPassword : null}>
          <PasswordInput autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
        </Field>
        <Field label="Powtórz nowe hasło" error={submitted ? errors.confirm : null}>
          <PasswordInput autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </Field>
        <PasswordChecklist checks={checks} />
        {formError && <FormMessage>{formError}</FormMessage>}
        <div>
          <Button type="submit" variant="primary" loading={changePassword.isPending} className={TOUCH_BUTTON}>
            Zmień hasło
          </Button>
        </div>
      </form>
    </Section>
  );
}

function deviceIcon(label: string) {
  return /android|iphone|ipad|ios|mobile|telefon/i.test(label) ? Smartphone : Monitor;
}

function DevicesSection() {
  const utils = trpc.useUtils();
  const sessions = trpc.identity.listSessions.useQuery();
  const revoke = trpc.identity.revokeSession.useMutation({
    onSuccess: () => {
      toast.success('Urządzenie wylogowane');
      void utils.identity.listSessions.invalidate();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const revokeOthers = trpc.identity.revokeOtherSessions.useMutation({
    onSuccess: () => {
      toast.success('Pozostałe urządzenia wylogowane');
      void utils.identity.listSessions.invalidate();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const signOut = useSignOut();
  const [confirmOthers, setConfirmOthers] = useState(false);

  const list: SessionListItem[] = sessions.data ?? [];
  const others = list.filter((s) => !s.current);
  const sorted = [...list].sort((a, b) => Number(b.current) - Number(a.current));

  return (
    <Section className="mt-6" aria-labelledby="devices">
      <SectionHeader
        tone="strong"
        title={<span id="devices">Urządzenia</span>}
        action={
          others.length > 0 ? (
            <Button variant="secondary" size="s" className={TOUCH_BUTTON} onClick={() => setConfirmOthers(true)}>
              Wyloguj pozostałe
            </Button>
          ) : undefined
        }
      />
      {sessions.isPending ? (
        <ListSkeleton rows={2} />
      ) : sessions.isError ? (
        <ErrorState compact onRetry={() => void sessions.refetch()} retrying={sessions.isFetching} />
      ) : sorted.length === 0 ? (
        <EmptyState compact title="Brak aktywnych sesji." />
      ) : (
        <div className="border-t border-line">
          {sorted.map((item) => {
            const Icon = deviceIcon(item.deviceLabel);
            return (
              <ListRow
                key={item.id}
                height="l"
                leading={<Icon size={18} strokeWidth={1.75} aria-hidden className="text-fg-muted" />}
                title={item.deviceLabel}
                description={[
                  item.ip,
                  item.current ? 'to urządzenie, aktywne teraz' : `ostatnio ${formatRelativePast(item.lastSeenAt)}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                trailing={
                  item.current ? (
                    <>
                      <Status tone="success" className="hidden md:inline-flex">
                        To urządzenie
                      </Status>
                      <Button variant="ghost" size="s" className={TOUCH_BUTTON} icon={LogOut} onClick={signOut}>
                        Wyloguj
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="ghost"
                      size="s"
                      className={TOUCH_BUTTON}
                      loading={revoke.isPending && revoke.variables?.id === item.id}
                      onClick={() => revoke.mutate({ id: item.id })}
                      aria-label={`Wyloguj: ${item.deviceLabel}`}
                    >
                      Wyloguj
                    </Button>
                  )
                }
              />
            );
          })}
        </div>
      )}

      <Dialog
        open={confirmOthers}
        onOpenChange={setConfirmOthers}
        width="s"
        title="Wylogować pozostałe urządzenia?"
        description={`Zostaniesz zalogowany tylko na tym urządzeniu. Pozostałe (${others.length}) będą wymagały ponownego logowania.`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmOthers(false)} className={TOUCH_BUTTON}>
              Anuluj
            </Button>
            <Button
              variant="primary"
              loading={revokeOthers.isPending}
              onClick={() => revokeOthers.mutate(undefined, { onSettled: () => setConfirmOthers(false) })}
              className={TOUCH_BUTTON}
            >
              Wyloguj pozostałe
            </Button>
          </>
        }
      />
    </Section>
  );
}
